// How long someone has been waited on: the attention set's turn starts
// (`AttentionMember.since`, from the engine's replay) as short text.
import { sameLogin } from './attention';
import { turn } from './stackRail';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * The time since `since` (ISO), rounded down: `long` gives the two largest
 * units ("2d 4h", "5h 12m", "12m"), `short` the largest alone ("2d"). Under
 * a minute (or a start in the future, a clock skew) is "<1m". `bound`
 * (the start is only the latest the turn can have begun) adds "+": at
 * least that long. Null when `since` is absent or unreadable.
 */
export function waitText(since: string | null | undefined, now: number, form: 'long' | 'short', bound = false): string | null {
  if (!since) return null;
  const t = Date.parse(since);
  if (Number.isNaN(t)) return null;
  const ms = Math.max(0, now - t);
  const d = Math.floor(ms / DAY);
  const h = Math.floor((ms % DAY) / HOUR);
  const m = Math.floor((ms % HOUR) / MINUTE);
  const [big, small] = d ? [`${d}d`, h ? `${h}h` : ''] : h ? [`${h}h`, m ? `${m}m` : ''] : m ? [`${m}m`, ''] : ['<1m', ''];
  return (form === 'long' && small ? `${big} ${small}` : big) + (bound ? '+' : '');
}

type Waiting = { login: string; since?: string; sinceBound?: boolean };

/**
 * The member waiting longest by known turn start, how long in short form
 * ("+" for a lower bound), and how many are waiting. `known` is false when
 * some member's start is unknown, or only a bound later than the best's:
 * that member may have waited longer, so this is only the longest known
 * wait. Null when no start is known.
 */
export function longestWait(members: readonly Waiting[], now: number): { login: string; text: string; of: number; known: boolean; bound: boolean } | null {
  let best: { m: Waiting; at: number } | null = null;
  let known = true;
  for (const m of members) {
    const at = m.since ? Date.parse(m.since) : NaN;
    if (Number.isNaN(at)) known = false;
    else if (!best || at < best.at) best = { m, at };
  }
  if (!best) return null;
  // Another member whose start is only a bound (later than the best's) may have waited longer.
  const at = best.at;
  if (members.some((m) => m !== best!.m && m.sinceBound && m.since && Date.parse(m.since) > at)) known = false;
  const bound = !!best.m.sinceBound;
  return { login: best.m.login, text: waitText(best.m.since, now, 'short', bound)!, of: members.length, known, bound };
}

/**
 * A dashboard row's wait: "waiting 2d" when it is the viewer's turn (since
 * the viewer's turn began) or only the author is waited on (since the
 * author's began); null otherwise, or when that start is unknown.
 */
export function rowWait(
  attention: readonly Waiting[] | null | undefined,
  author: string | null,
  me: string,
  now: number,
): string | null {
  const t = turn(attention ?? null, author, me);
  const who = t.kind === 'you' ? me : t.kind === 'author' ? author : null;
  const m = who ? attention?.find((x) => sameLogin(x.login, who)) : undefined;
  const text = waitText(m?.since, now, 'short', !!m?.sinceBound);
  return text ? `waiting ${text}` : null;
}
